/**
 * Core command registrations. Imported once from App; features register
 * more of their own (daily notes, import, sync) in later phases.
 */
import { registerCommand } from "./registry";
import { usePalette } from "@/store/palette";
import { useTabs } from "@/store/tabs";
import { useUi } from "@/store/ui";
import { useExpanded } from "@/store/expanded";
import { stripMarkdownEscapes } from "@hydra-notes/shared";
import { createNote, createFolder, openItem, archiveItem } from "./nav";
import { useArchiveDialog } from "@/components/ArchiveDialog";
import { getEditor } from "@/editor/registry";
import { openDailyNote, ensureTemplatesFolder } from "./templates";
import { useSignInDialog } from "@/components/SignInDialog";
import { useHistoryDialog } from "@/components/HistoryDialog";
import { useTrashDialog } from "@/components/TrashDialog";
import { useImportDialog } from "@/components/ImportDialog";
import { useSettingsDialog } from "@/components/SettingsDialog";
import { useDocsDialog } from "@/developers/DocsDialog";
import { getData } from "@/data/store";
import { useAuth } from "@/data/auth";
import { exportNote, exportZip } from "@/data/export";
import { toast } from "@/ui/toast";

registerCommand({
  id: "palette.switcher",
  title: "Quick switcher: open or create note",
  defaultKeys: "$mod+p",
  section: "Navigate",
  run: () => usePalette.getState().open("switcher"),
});

registerCommand({
  id: "palette.commands",
  title: "Command palette",
  defaultKeys: "$mod+Shift+p",
  section: "Navigate",
  run: () => usePalette.getState().open("commands"),
});

registerCommand({
  id: "note.new",
  title: "New note",
  defaultKeys: "$mod+n",
  section: "Notes",
  run: () => createNote(null),
});

registerCommand({
  id: "folder.new",
  title: "New folder",
  defaultKeys: "$mod+Shift+n",
  section: "Notes",
  run: () => createFolder(null),
});

registerCommand({
  id: "tab.close",
  title: "Close tab",
  defaultKeys: "$mod+w",
  section: "Tabs",
  run: () => {
    const t = useTabs.getState();
    const active = t.activeTab();
    if (active) t.closeTab(active.group.id, active.tab.id);
  },
});

registerCommand({
  id: "tab.next",
  title: "Next tab",
  defaultKeys: "$mod+Alt+ArrowRight",
  section: "Tabs",
  run: () => useTabs.getState().cycleTab(1),
});

registerCommand({
  id: "tab.prev",
  title: "Previous tab",
  defaultKeys: "$mod+Alt+ArrowLeft",
  section: "Tabs",
  run: () => useTabs.getState().cycleTab(-1),
});

registerCommand({
  id: "tab.split",
  title: "Split right",
  defaultKeys: "$mod+\\",
  section: "Tabs",
  run: () => {
    const t = useTabs.getState();
    const active = t.activeTab();
    if (active) t.splitRight(active.group.id, active.tab.id);
  },
});

registerCommand({
  id: "ui.toggleTree",
  title: "Toggle sidebar",
  defaultKeys: "$mod+b",
  section: "View",
  run: () => useUi.getState().toggleTree(),
});

registerCommand({
  id: "ui.toggleRight",
  title: "Toggle details panel",
  defaultKeys: "$mod+Shift+b",
  section: "View",
  run: () => useUi.getState().toggleRight(),
});

registerCommand({
  id: "tree.collapseAll",
  title: "Collapse all folders",
  section: "View",
  run: () => useExpanded.getState().collapseAll(),
});

registerCommand({
  id: "ui.focusMode",
  title: "Toggle focus mode",
  defaultKeys: "$mod+Shift+f",
  section: "View",
  run: () => useUi.getState().toggleFocusMode(),
});

registerCommand({
  id: "note.print",
  title: "Print current note",
  section: "Notes",
  run: () => window.print(),
});

registerCommand({
  id: "note.daily",
  title: "Open today's daily note",
  defaultKeys: "$mod+d",
  section: "Notes",
  run: () => void openDailyNote(),
});

registerCommand({
  id: "note.fromTemplate",
  title: "New note from template",
  defaultKeys: "$mod+Alt+n",
  section: "Notes",
  run: () => usePalette.getState().open("template"),
});

registerCommand({
  id: "templates.folder",
  title: "Open Templates folder",
  section: "Notes",
  run: () => openItem(ensureTemplatesFolder()),
});

registerCommand({
  id: "note.history",
  title: "Version history for current note",
  defaultKeys: "$mod+Alt+h",
  section: "Notes",
  run: () => {
    const active = useTabs.getState().activeTab();
    if (!active) return;
    const item = getData().workspace.getItem(active.tab.itemId);
    if (item?.type === "note") useHistoryDialog.getState().open(item.id);
  },
});

registerCommand({
  id: "trash.open",
  title: "Open trash",
  section: "Notes",
  run: () => useTrashDialog.getState().setOpen(true),
});

registerCommand({
  id: "tags.browse",
  title: "Browse all tags",
  section: "Navigate",
  run: () => {
    void import("@/components/TagBrowser").then((m) => m.useTagBrowser.getState().setOpen(true));
  },
});

registerCommand({
  id: "note.pin",
  title: "Pin / unpin current note",
  section: "Notes",
  run: () => {
    const active = useTabs.getState().activeTab();
    if (!active) return;
    const item = getData().workspace.getItem(active.tab.itemId);
    if (item) getData().workspace.setPinned(item.id, !item.pinned);
  },
});

registerCommand({
  id: "archive.open",
  title: "Open archive",
  section: "Notes",
  run: () => useArchiveDialog.getState().setOpen(true),
});

registerCommand({
  id: "note.archive",
  title: "Archive current note",
  section: "Notes",
  run: () => {
    const active = useTabs.getState().activeTab();
    if (active) archiveItem(active.tab.itemId);
  },
});

registerCommand({
  id: "settings.open",
  title: "Open settings",
  defaultKeys: "$mod+,",
  section: "View",
  run: () => useSettingsDialog.getState().setOpen(true),
});

registerCommand({
  id: "note.cleanEscapes",
  title: "Remove escape backslashes from current note",
  section: "Data",
  run: () => {
    const active = useTabs.getState().activeTab();
    if (!active) return;
    const item = getData().workspace.getItem(active.tab.itemId);
    if (item?.type !== "note") return;
    const view = getEditor(item.id);
    if (view) {
      const cleaned = stripMarkdownEscapes(view.state.doc.toString());
      if (cleaned !== view.state.doc.toString()) {
        view.dispatch({ changes: { from: 0, to: view.state.doc.length, insert: cleaned } });
        toast.success("Escape backslashes removed (Ctrl+Z to undo)");
      } else {
        toast.info("No escape backslashes found");
      }
    }
  },
});

registerCommand({
  id: "workspace.cleanEscapes",
  title: "Remove escape backslashes from ALL notes",
  section: "Data",
  run: () => {
    if (!confirm("Strip importer escape backslashes (\\. \\- \\[ …) from every note? Code blocks are untouched. This edits notes in place.")) return;
    void (async () => {
      const contexts = [getData()];
      // shared folders join only when their OWNER explicitly opts in: this
      // rewrites notes for every member
      const { useSharedFolders } = await import("@/data/sharedFolders");
      const { ctxFor } = await import("@/data/store");
      const owned = useSharedFolders.getState().folders.filter((f) => f.role === "owner" && !f.locked);
      if (owned.length > 0) {
        const names = owned.map((f) => `"${f.title}"`).join(", ");
        if (confirm(`Also clean the shared folders you own (${names})? Every member sees those edits.`)) {
          for (const f of owned) {
            const ctx = ctxFor(f.id);
            if (ctx) contexts.push(ctx);
          }
        }
      }
      let changed = 0;
      for (const data of contexts) {
        const notes = data.workspace.getSnapshot().filter((i) => i.type === "note");
        for (const item of notes) {
          const handle = data.notes.open(item.id);
          await handle.whenLoaded;
          const current = handle.text.toString();
          const cleaned = stripMarkdownEscapes(current);
          if (cleaned !== current) {
            handle.doc.transact(() => {
              handle.text.delete(0, handle.text.length);
              handle.text.insert(0, cleaned);
            });
            data.workspace.touch(item.id);
            changed++;
          }
          handle.release();
        }
      }
      toast.success(changed ? `Cleaned ${changed} note${changed === 1 ? "" : "s"}` : "No escape backslashes found anywhere");
    })();
  },
});

registerCommand({
  id: "note.share",
  title: "Share current note (public link)",
  section: "Notes",
  run: () => {
    const active = useTabs.getState().activeTab();
    if (!active) return;
    const item = getData().workspace.getItem(active.tab.itemId);
    if (item?.type === "note") {
      void import("@/components/ShareDialog").then((m) => m.useShareDialog.getState().open(item.id));
    }
  },
});

registerCommand({
  id: "workspace.findReplace",
  title: "Find and replace in all notes",
  section: "Data",
  run: () => {
    void import("@/components/FindReplaceDialog").then((m) => m.useFindReplace.getState().setOpen(true));
  },
});

registerCommand({
  id: "import.open",
  title: "Import notes or tasks (markdown, Obsidian, Notion, Trilium, Evernote, Vikunja)",
  section: "Data",
  run: () => useImportDialog.getState().setOpen(true),
});

registerCommand({
  id: "export.current",
  title: "Export current note as markdown",
  section: "Data",
  run: () => {
    const active = useTabs.getState().activeTab();
    if (!active) return;
    const item = getData().workspace.getItem(active.tab.itemId);
    if (item?.type === "note") void exportNote(item.id);
    else if (item?.type === "folder") void exportZip(item.id).then((n) => toast.success(`Exported ${n} notes`));
  },
});

registerCommand({
  id: "export.all",
  title: "Export all notes as zip",
  section: "Data",
  run: () => void exportZip(null).then((n) => toast.success(`Exported ${n} notes`)),
});

registerCommand({
  id: "account.signIn",
  title: "Sign in / back up notes",
  section: "Account",
  run: () => {
    if (useAuth.getState().status !== "signedIn") useSignInDialog.getState().setOpen(true);
  },
});

registerCommand({
  id: "account.signOut",
  title: "Sign out",
  section: "Account",
  run: () => void useAuth.getState().signOut(),
});

registerCommand({
  id: "chat.workspace",
  title: "Ask Claude about this workspace",
  defaultKeys: "$mod+j",
  section: "Claude",
  run: () => void import("@/components/ProjectChatDialog").then((m) => m.useProjectChat.getState().setOpen(true)),
});

registerCommand({
  id: "chat.note",
  title: "Ask Claude about this note",
  defaultKeys: "$mod+shift+j",
  section: "Claude",
  run: () => {
    const active = useTabs.getState().activeTab();
    const item = active ? getData().workspace.getItem(active.tab.itemId) : undefined;
    useUi.getState().openRight("chat", item?.type === "note" ? "note" : "workspace");
  },
});

registerCommand({
  id: "dev.docs",
  title: "Developer documentation (API and MCP)",
  section: "Help",
  run: () => useDocsDialog.getState().setOpen(true),
});

registerCommand({
  id: "dev.connect",
  title: "Connect an assistant (Claude Code, MCP)",
  section: "Help",
  run: () => useSettingsDialog.getState().setOpen(true, "developers"),
});
